import React, { useState, useEffect } from 'react'
import ResumeProvider, { useResume } from './ResumeProvider'
import {
  TemplateProvider,
  useTemplate,
  initializeTemplates,
  availableFontNames,
} from '~/lib/pdf'
import { Textarea } from '~/lib/ui/textarea'
import { Button } from '~/lib/ui/button'
import { Toaster } from '~/lib/ui/toast'
import {
  TabNav,
  TabPanel,
  PanelContainer,
  ButtonContainer,
  TabButton,
} from '~/components/shared/TabNav'
import {
  DocumentMagnifyingGlassIcon,
  IdentificationIcon,
  ClipboardDocumentCheckIcon,
  DocumentTextIcon,
} from '@heroicons/react/24/outline'
import Editor from './Editor/index'
import PdfViewerPanel from './PdfViewerPanel'
import StyleEditor from './StyleEditor'
import * as T0 from './Template0'
import * as T1 from './Template1'
import * as T2 from './Template2'

// These need to be declared outside of a component so they can't affect rendering
const [templates, templateStyle, templateSupportedStyles] = initializeTemplates(
  [T0, T1, T2],
)

const ResumeBuilder = ({ template_settings, template_index, ...props }) => {
  const templateKey = `template-${template_index}`

  // When a key is removed from default template settings, remove it from stored settings
  const outdatedTemplateKeys = template_settings
    ? Object.keys(template_settings).filter(
        (key) => !templateSupportedStyles[templateKey]?.includes(key),
      )
    : []
  outdatedTemplateKeys.forEach((key) => {
    delete template_settings?.[key]
  })

  // If a font name stored for a template is not registered, set it to the default font
  const fontKeys = ['primaryFontFamily', 'secondaryFontFamily']
  fontKeys.forEach((key) => {
    if (
      template_settings &&
      Object.keys(template_settings).includes(key) &&
      Object.keys(templateStyle[templateKey]).includes(key) &&
      !availableFontNames.includes(template_settings[key])
    ) {
      template_settings[key] = templateStyle[templateKey][key]
    }
  })

  const defaultStyle = template_settings
    ? {
        ...templateStyle,
        [templateKey]: {
          ...templateStyle[templateKey],
          ...template_settings,
        },
      }
    : templateStyle
  const templateProps = {
    templates,
    defaultStyle,
    templateSupportedStyles,
    template_index,
    dev_mode: props.dev_mode,
  }

  return (
    <TemplateProvider {...templateProps}>
      <ResumeProvider {...props}>
        <ResumeBuilderInner />
      </ResumeProvider>
    </TemplateProvider>
  )
}
export default ResumeBuilder

const ResumeBuilderInner = () => {
  const { devMode, documentId } = useResume()

  const defaultActiveTab = documentId ? 'details' : 'chooseTemplate'

  return (
    <div className="flex w-full flex-col md:flex-row">
      <div className="md:basis-3/5">
        <TabNav
          className="px-4 pt-4 lg:px-8 xl:px-12"
          defaultActiveTab={defaultActiveTab}
          mobileMode="swipe"
        >
          <ButtonContainer>
            <TabButton
              name="chooseTemplate"
              text="Choose Template"
              Icon={DocumentMagnifyingGlassIcon}
            />
            <TabButton
              name="details"
              text="Details"
              Icon={IdentificationIcon}
            />
            <TabButton
              name="formattingAndDesign"
              text="Formatting & Design"
              Icon={ClipboardDocumentCheckIcon}
            />
            {devMode && (
              <TabButton name="text" text="Raw JSON" Icon={DocumentTextIcon} />
            )}
          </ButtonContainer>
          <PanelContainer className="md:min-h-[91vh]">
            <TabPanel name="chooseTemplate" className="bg-[#E0F2F9] pt-8">
              <SelectTemplatePanel />
            </TabPanel>
            <TabPanel name="details" className="pt-8">
              <Editor />
            </TabPanel>
            <TabPanel name="formattingAndDesign" className="pt-8">
              <StyleEditor />
            </TabPanel>
            <TabPanel name="text" className="pt-8">
              <TextEditor />
            </TabPanel>
          </PanelContainer>
        </TabNav>
      </div>
      <div id="resume-pdf-preview" className="relative md:flex-grow">
        <div className="w-full md:sticky md:left-0 md:top-0 md:h-screen">
          <PdfViewerPanel />
        </div>
      </div>
      <Toaster />
    </div>
  )
}

const SelectTemplatePanel = () => {
  const { templateIndex, setTemplateIndex } = useTemplate()
  const templateIndices = [0, 1, 2]
  const activeClasses = 'font-roboto-slab text-primary font-bold pb-2'
  const inactiveClasses = 'font-roboto-slab text-primary pb-2'

  return (
    <div className="grid h-full sm:grid-cols-2 lg:grid-cols-3">
      {templateIndices.map((index) => (
        <div className="flex flex-col items-center" key={`template-${index}`}>
          <span
            className={templateIndex == index ? activeClasses : inactiveClasses}
          >
            Style {index + 1}
          </span>
          <button
            onClick={() => setTemplateIndex(index)}
            disabled={templateIndex === index}
            className={
              templateIndex === index
                ? 'rounded-xl border-4 border-solid border-primary'
                : 'm-1 rounded-xl'
            }
          >
            <TemplatePreview
              className="w-48 rounded-xl"
              templateIndex={index}
            />
          </button>
        </div>
      ))}
    </div>
  )
}

const TemplatePreview = ({ className, templateIndex }) => {
  const url = `https://www-yns-com.s3.amazonaws.com/resume_builder/Template${templateIndex}.png`
  return (
    <img className={className} src={url} alt={`Template ${templateIndex}`} />
  )
}

const TextEditor = () => {
  const { resumeDocument, setResumeDocument } = useResume()
  const [editorResume, setEditorResume] = useState(
    JSON.stringify(resumeDocument, null, 2),
  )

  const sync = () => {
    const updatedResume = JSON.parse(editorResume)
    setResumeDocument(updatedResume)
  }

  useEffect(() => {
    setEditorResume(JSON.stringify(resumeDocument, null, 2))
  }, [resumeDocument])

  return (
    <div className="flex h-full flex-col">
      <div className="flex flex-row justify-evenly">
        <Button onClick={sync}>Sync</Button>
      </div>
      <Textarea
        className="h-full w-full"
        value={editorResume}
        onChange={(e) => setEditorResume(e.target.value)}
      ></Textarea>
    </div>
  )
}
